// Phase 9 — Dashboard-first UI rebalance. Module summary card tile.
//
// Renders one card per enabled module in the dashboard grid. Each card is
// equal-sized, priority-colored on the left accent, and clickable to navigate
// to the module's full view.

import { useNavigate } from 'react-router-dom';
import type { DashboardCard } from '@dailydeck/shared/types';
import { cn } from '../../lib/cn.js';

const PRIORITY_ACCENT: Record<string, string> = {
  critical: 'border-l-state-danger',
  important: 'border-l-state-warning',
  info: 'border-l-border-subtle',
};

function moduleIcon(moduleId: string): string {
  const icons: Record<string, string> = {
    payments: '💰',
    dates: '📅',
    'platform-meta': '⚙️',
    tasks: '📋',
    habits: '✅',
    notes: '📝',
    goals: '🎯',
    reading: '📚',
    subscriptions: '🔁',
    travel: '✈️',
    inventory: '🏠',
  };
  return icons[moduleId] ?? '📦';
}

export interface ModuleCardProps {
  readonly card: DashboardCard;
}

export function ModuleCard({ card }: ModuleCardProps): JSX.Element {
  const navigate = useNavigate();
  const accent = PRIORITY_ACCENT[card.priority] ?? PRIORITY_ACCENT.info;
  const href = card.action_url ?? `/${card.module_id === 'payments' ? 'payments' : card.module_id === 'dates' ? 'dates' : ''}`;

  return (
    <button
      type="button"
      onClick={() => navigate(href)}
      className={cn(
        'group flex flex-col gap-2 rounded-lg border border-border-subtle bg-surface-raised p-4 text-left',
        'border-l-4',
        accent,
        'transition-colors duration-fast hover:bg-accent-subtle hover:border-l-accent-default',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-default focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
      )}
      data-testid={`module-card-${card.module_id}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-lg" aria-hidden="true">
          {moduleIcon(card.module_id ?? '')}
        </span>
        <span className="text-body-strong text-text-primary font-medium">
          {card.title}
        </span>
      </div>
      {card.subtitle && (
        <p className="text-caption text-text-secondary">{card.subtitle}</p>
      )}
      {card.value !== undefined && card.value !== null && (
        <p className="text-title-3 font-semibold text-text-primary tabular-nums">
          {card.value}
        </p>
      )}
      {card.badge && (
        <span className="inline-flex self-start rounded-full bg-accent-subtle px-2 py-0.5 text-micro font-medium text-accent-default">
          {card.badge}
        </span>
      )}
    </button>
  );
}
